import React, { useEffect } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { useAuthorizeAdmin } from "../../hooks/api/useAdminAuth";
import { ROUTE_LOGIN } from "../../constants/routes";
import { fullPageCentered } from "../../styles/layoutStyles";

/**
 * Wraps admin routes: ensures user is authenticated and has super-admin role.
 * If not authenticated, redirects to login. If authenticated but not super-admin, shows access denied.
 * When authenticated, renders child routes via <Outlet />.
 * Shows a loader while the initial authorize check is in progress (avoids flashing login on refresh).
 */
const ProtectedAdminRoute: React.FC = () => {
  const { status, setLoading } = useAdminAuth();
  const authorizeMutation = useAuthorizeAdmin();
  const location = useLocation();

  useEffect(() => {
    if (status === "unauthenticated" && !authorizeMutation.isPending) {
      setLoading(true);
      authorizeMutation.mutate(undefined as unknown as void, {
        onSettled: () => setLoading(false),
      });
    }
  }, []);

  const isCheckingAuth =
    status === "loading" ||
    authorizeMutation.isPending ||
    (status === "unauthenticated" && authorizeMutation.isIdle);

  if (isCheckingAuth) {
    return (
      <Box
        sx={{
          ...fullPageCentered,
          gap: 2,
          background:
            "linear-gradient(160deg, #f5f7fa 0%, #e8eef5 50%, #dde5f0 100%)",
        }}
      >
        <CircularProgress size={40} thickness={4} />
        <Typography color="text.secondary" variant="body2">
          Checking authentication…
        </Typography>
      </Box>
    );
  }

  if (status === "access_denied") {
    return (
      <Box sx={fullPageCentered}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Access denied
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your account does not have admin access. Use the main application
            for company and store operations.
          </Typography>
          <Button variant="contained" href="/" sx={{ textTransform: "none" }}>
            Go to main app
          </Button>
        </motion.div>
      </Box>
    );
  }

  if (status !== "authenticated") {
    return <Navigate to={ROUTE_LOGIN} state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export default ProtectedAdminRoute;
